// File: frontend/src/components/entries/TokenPromptModal.tsx

import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { MdKeyboard } from "react-icons/md";
import { RiDeleteBin6Line } from "react-icons/ri";
// Circular module reference (EntryEditorToolbar renders this modal) — safe:
// both bindings are only dereferenced at render time, after module evaluation.
import { EntryEditorToolbar } from "./EntryEditorToolbar";

interface TokenPromptModalProps {
  /** i18n key for the modal title */
  titleKey: string;
  /** i18n key for the textarea placeholder */
  placeholderKey: string;
  /** Token keyword the text is wrapped with — "alinti" or "spoiler" */
  keyword: "alinti" | "spoiler";
  onInsert: (text: string) => void;
  onClose: () => void;
}

// Turkish special characters offered in the modal keyboard —
// lowercase only, consistent with the entry form keyboard
const MODAL_TURKISH_CHARS = ["ç", "ğ", "ı", "ö", "ş", "ü"];

/**
 * Centered modal for writing/pasting a block token (alıntı or spoiler).
 * Reuses the original entry toolbar (refs, bold, italic, bağlantı, görsel) so
 * block contents are not limited to plain text — only the block buttons
 * themselves are hidden to prevent nesting, and there is no draft button.
 * The bottom row mirrors the entry form: Turkish keyboard + clear on the left,
 * the insert button on the right.
 */
export const TokenPromptModal: React.FC<TokenPromptModalProps> = ({
  titleKey,
  placeholderKey,
  keyword,
  onInsert,
  onClose,
}) => {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [showTrKeyboard, setShowTrKeyboard] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Wraps the keyboard toggle + Turkish character popup — used to close the
  // popup when the user clicks anywhere outside it
  const trKeyboardRef = useRef<HTMLDivElement>(null);

  // Focus the textarea when the modal opens
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Close the Turkish character keyboard on any outside click
  useEffect(() => {
    if (!showTrKeyboard) return;
    const onMouseDown = (e: MouseEvent) => {
      if (trKeyboardRef.current && !trKeyboardRef.current.contains(e.target as Node)) {
        setShowTrKeyboard(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [showTrKeyboard]);

  /** Insert a Turkish character at the current cursor position. */
  const insertModalChar = (char: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    setText(text.slice(0, start) + char + text.slice(end));
    requestAnimationFrame(() => {
      textarea.selectionStart = start + char.length;
      textarea.selectionEnd = start + char.length;
      textarea.focus();
    });
  };

  const handleInsert = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    // Format: (keyword: text) — rendered as a block by EntryRow/previews
    onInsert(`(${keyword}: ${trimmed})`);
    onClose();
  };

  // Rendered through a portal so no parent overflow/z-index can clip the modal
  return ReactDOM.createPortal(
    <div
      className="quote-modal-overlay"
      onMouseDown={(e) => {
        // Close when clicking the dimmed backdrop (not the modal card itself)
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="quote-modal">
        <div className="ref-popup-header">
          <span className="ref-popup-title">{t(titleKey)}</span>
          <button type="button" className="ref-popup-close" onClick={onClose} title={t("close")}>✕</button>
        </div>

        {/* Original entry toolbar — block buttons hidden to prevent nesting */}
        <div className="quote-modal-toolbar">
          <EntryEditorToolbar
            textareaRef={textareaRef}
            content={text}
            setContent={setText}
            showQuoteButton={false}
            showSpoilerButton={false}
          />
        </div>

        <textarea
          ref={textareaRef}
          className="quote-modal-textarea"
          placeholder={t(placeholderKey)}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />

        {/* Bottom row — mirrors the entry form: keyboard + clear left, insert right */}
        <div className="quote-modal-actions">
          <div className="epf-keyboard-wrapper" ref={trKeyboardRef}>
            {/* Turkish keyboard toggle button */}
            <button
              type="button"
              className={`epf-keyboard-btn${showTrKeyboard ? " epf-keyboard-btn--active" : ""}`}
              title={t("turkish_char_keyboard", "Turkish character keyboard")}
              onClick={() => setShowTrKeyboard((prev) => !prev)}
            >
              <MdKeyboard size={16} />
            </button>

            {/* Clear content button — only visible when there is something to clear */}
            {text.length > 0 && (
              <button
                type="button"
                className="epf-clear-btn"
                title={t("admin_clear_filters", "Clear")}
                onClick={() => {
                  setText("");
                  textareaRef.current?.focus();
                }}
              >
                <RiDeleteBin6Line size={16} />
              </button>
            )}

            {/* Turkish character popup — floats above the keyboard button */}
            {showTrKeyboard && (
              <div className="epf-tr-keyboard">
                {MODAL_TURKISH_CHARS.map((char) => (
                  <button
                    key={char}
                    type="button"
                    className="epf-tr-char-btn"
                    onClick={() => insertModalChar(char)}
                  >
                    {char}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            type="button"
            className="link-popup-btn"
            onClick={handleInsert}
            disabled={!text.trim()}
          >
            {t("add_btn")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};